/**
 * React Query hook for fetching a single story's details
 * Story 199: View story details with PRD and design
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/** Full story details from API */
export interface StoryDetail {
  id: string;
  external_id: string;
  title: string;
  status: 'proposed' | 'in-progress' | 'qa' | 'done' | 'blocked' | 'paused';
  priority: number;
  epic_slug: string | null;
  assignee: string | null;
  created_at: string;
  updated_at: string;
  qa_evidence?: Record<string, string>; // Story 274: QA workflow evidence
}

/** Query key factory for story details */
export const storyDetailKeys = {
  all: ['story-detail'] as const,
  detail: (projectSlug: string, storyId: string) =>
    [...storyDetailKeys.all, projectSlug, storyId] as const,
};

/** Fetch a single story's details */
async function fetchStory(
  projectSlug: string,
  storyId: string
): Promise<StoryDetail> {
  const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}`;
  return api.fetch(endpoint);
}

/**
 * Hook to fetch a single story's details
 *
 * @param projectSlug - Project identifier (e.g., "boxabirds/claude-backlog")
 * @param storyId - Story external ID
 */
export function useStory(projectSlug: string, storyId: string | null) {
  return useQuery({
    queryKey: storyDetailKeys.detail(projectSlug, storyId ?? ''),
    queryFn: () => fetchStory(projectSlug, storyId!),
    enabled: !!projectSlug && !!storyId,
  });
}
